import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, withQuery, money, quantity, date } from '../api.js';
import { useAuth } from '../App.jsx';
import Lookup from '../components/Lookup.jsx';
import ProductLookup from '../components/ProductLookup.jsx';

export const PO_STATUS_LABEL = {
  draft: 'чернова',
  sent: 'изпратена',
  received: 'получена',
  cancelled: 'отказана',
};
const PO_STATUS_CLASS = {
  draft: 'badge',
  sent: 'badge warn',
  received: 'badge ok',
  cancelled: 'badge muted',
};

/**
 * Поръчки към доставчик.
 *
 * „Предложение" показва стоките под минималното количество, групирани по
 * последния доставчик, с количество до максимума. Едно щракване прави
 * чернова на поръчка за целия доставчик — отметнатите редове, с
 * количествата, каквито са на екрана.
 */
export default function PurchaseOrders() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'suggest';
  return (
    <>
      <div className="page-head">
        <h1>Поръчки към доставчик</h1>
        <div className="spacer" />
        <button className={tab === 'suggest' ? 'primary' : ''}
          onClick={() => setParams({ tab: 'suggest' })}>Предложение за поръчка</button>
        <button className={tab === 'orders' ? 'primary' : ''}
          onClick={() => setParams({ tab: 'orders' })}>Поръчки</button>
      </div>
      {tab === 'suggest' ? <Suggestions /> : <OrderList />}
    </>
  );
}

function Suggestions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [kolichestva, setKolichestva] = useState({});
  const [izbrani, setIzbrani] = useState({});
  const [dostavchik, setDostavchik] = useState({});
  const [busy, setBusy] = useState(false);
  const [vest, setVest] = useState(null);
  const [filtar, setFiltar] = useState('');

  function zaredi() {
    setRows(null);
    api.get('/api/purchase-orders/suggestions')
      .then((r) => {
        setRows(r);
        setKolichestva(Object.fromEntries(r.map((x) => [x.product_id, x.predlozheno])));
        setIzbrani(Object.fromEntries(r.map((x) => [x.product_id, true])));
      })
      .catch((e) => setError(e.message));
  }
  useEffect(zaredi, []);

  const grupi = useMemo(() => {
    if (!rows) return [];
    const f = filtar.trim().toLowerCase();
    const m = new Map();
    for (const r of rows) {
      if (f && !`${r.name} ${r.code ?? ''} ${r.supplier_name ?? ''} ${r.group_name}`.toLowerCase().includes(f)) continue;
      const k = r.supplier_id ?? 0;
      if (!m.has(k)) m.set(k, { id: r.supplier_id, name: r.supplier_name ?? 'Без доставка досега', rows: [] });
      m.get(k).rows.push(r);
    }
    return [...m.values()];
  }, [rows, filtar]);

  async function napraviPoruchka(g) {
    const partnerId = g.id ?? dostavchik[0]?.id;
    if (!partnerId) { setError('Избери доставчик за тези стоки'); return; }
    const lines = g.rows
      .filter((r) => izbrani[r.product_id] && Number(kolichestva[r.product_id]) > 0)
      .map((r) => ({
        productId: r.product_id,
        quantity: Number(kolichestva[r.product_id]),
        price: r.last_price != null ? Number(r.last_price) : null,
      }));
    if (!lines.length) { setError('Няма отметнати редове'); return; }
    setBusy(true);
    setError(null);
    try {
      const o = await api.post('/api/purchase-orders', { partnerId, lines });
      navigate(`/purchase-orders/${o.id}`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function preizchisli() {
    if (!window.confirm('Минималните количества на всички стоки ще се сметнат наново '
      + 'от продажбите за последните 3 години (средно за месец). '
      + 'Ръчно въведените ще се заменят. Продължаваш ли?')) return;
    setBusy(true);
    try {
      const r = await api.post('/api/purchase-orders/recalc-min', { godini: 3, meseci: 1 });
      setVest(`Сметнато наново: ${r.s_minimum} стоки имат минимално количество.`);
      zaredi();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (!rows && !error) return <div className="card"><div className="empty">Зареждане…</div></div>;

  return (
    <>
      <div className="toolbar">
        <label className="field" style={{ minWidth: 260 }}>
          Търсене
          <input value={filtar} onChange={(e) => setFiltar(e.target.value)}
            placeholder="стока, група или доставчик" autoComplete="off" />
        </label>
        <div className="spacer" />
        {rows && <span className="badge">{rows.length} стоки под минимума</span>}
        {user.role === 'admin' && (
          <button type="button" onClick={preizchisli} disabled={busy}
            title="Минимум = средна месечна продажба за последните 3 години; максимум = два пъти минимума">
            Пресметни минимумите от продажбите
          </button>
        )}
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Минимумът на стоката е средната ѝ месечна продажба за последните 3 години,
        максимумът — двойно. Предлага се допълване до максимума, като се брои
        наличността във всички складове и вече поръчаното. Минимумът на отделна
        стока се сменя от картата ѝ.
      </p>
      {vest && <div className="notice">{vest}</div>}
      {error && <div className="error">{error}</div>}

      {rows && grupi.length === 0 && (
        <div className="card"><div className="empty">Няма стоки под минималното количество</div></div>
      )}

      {grupi.map((g) => {
        const otmetnati = g.rows.filter((r) => izbrani[r.product_id]);
        const suma = otmetnati.reduce((s, r) =>
          s + Number(kolichestva[r.product_id] || 0) * Number(r.last_price || 0), 0);
        return (
          <div className="card" key={g.id ?? 0}>
            <div className="toolbar" style={{ marginBottom: 6 }}>
              {g.id
                ? <strong><Link to={`/partners/${g.id}`}>{g.name}</Link></strong>
                : (
                  <label className="field" style={{ minWidth: 300 }}>
                    {g.name} — от кого да се поръча?
                    <Lookup url="/api/partners" value={dostavchik[0]?.id ?? null}
                      label={dostavchik[0]?.name ?? ''} placeholder="доставчик"
                      onPick={(id, name) => setDostavchik({ 0: id ? { id, name } : null })} />
                  </label>
                )}
              <span className="muted">{g.rows.length} стоки</span>
              <div className="spacer" />
              <span className="muted">≈ {money(suma)} без ДДС</span>
              <button type="button" className="primary" disabled={busy || !otmetnati.length}
                onClick={() => napraviPoruchka(g)}>
                Поръчка ({otmetnati.length})
              </button>
            </div>
            <table className="compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input type="checkbox"
                      checked={otmetnati.length === g.rows.length}
                      onChange={(e) => setIzbrani((s) => ({
                        ...s,
                        ...Object.fromEntries(g.rows.map((r) => [r.product_id, e.target.checked])),
                      }))} />
                  </th>
                  <th>Стока</th>
                  <th>Група</th>
                  <th className="num">Наличност</th>
                  <th className="num">Поръчано</th>
                  <th className="num">Мин.</th>
                  <th className="num">Макс.</th>
                  <th className="num">Последна цена</th>
                  <th className="num" style={{ width: 110 }}>Поръчай</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <tr key={r.product_id} className={izbrani[r.product_id] ? '' : 'muted'}>
                    <td>
                      <input type="checkbox" checked={!!izbrani[r.product_id]}
                        onChange={(e) => setIzbrani((s) => ({ ...s, [r.product_id]: e.target.checked }))} />
                    </td>
                    <td><Link to={`/products/${r.product_id}`}>{r.name}</Link></td>
                    <td className="muted">{r.group_name}</td>
                    <td className="num">{quantity(r.nalichnost)}</td>
                    <td className="num">{Number(r.porachano) ? quantity(r.porachano) : ''}</td>
                    <td className="num">{quantity(r.min_qty)}</td>
                    <td className="num">{quantity(r.max_qty)}</td>
                    <td className="num">{r.last_price != null ? money(r.last_price) : ''}</td>
                    <td className="num">
                      <input type="number" min="0" step="1" style={{ width: 80, textAlign: 'right' }}
                        value={kolichestva[r.product_id] ?? ''}
                        onChange={(e) => setKolichestva((s) => ({ ...s, [r.product_id]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

function OrderList() {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(withQuery('/api/purchase-orders', status ? { status } : {}))
      .then(setRows)
      .catch((e) => setError(e.message));
  }, [status]);

  return (
    <>
      <div className="toolbar">
        {[['open', 'Отворени'], ['received', 'Получени'], ['cancelled', 'Отказани'], ['', 'Всички']]
          .map(([k, l]) => (
            <button key={k} type="button" className={status === k ? 'primary' : ''}
              onClick={() => setStatus(k)}>{l}</button>
          ))}
      </div>
      {error && <div className="error">{error}</div>}
      <div className="card">
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 70 }}>№</th>
              <th style={{ width: 95 }}>Дата</th>
              <th>Доставчик</th>
              <th>Статус</th>
              <th className="num">Редове</th>
              <th className="num">≈ Стойност</th>
              <th>Доставка</th>
              <th>Създал</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={8} className="empty">Зареждане…</td></tr>}
            {rows?.map((o) => (
              <tr key={o.id}>
                <td><Link to={`/purchase-orders/${o.id}`}>{o.order_no}</Link></td>
                <td>{date(o.order_date)}</td>
                <td><Link to={`/purchase-orders/${o.id}`}>{o.partner_name}</Link></td>
                <td><span className={PO_STATUS_CLASS[o.status]}>{PO_STATUS_LABEL[o.status]}</span></td>
                <td className="num">{o.line_count}</td>
                <td className="num">{o.approx_total != null ? money(o.approx_total) : ''}</td>
                <td>
                  {o.purchase_document_id
                    ? <Link to={`/documents/${o.purchase_document_id}`}>{o.purchase_doc_number}</Link>
                    : ''}
                </td>
                <td className="muted">{o.created_by_name}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={8} className="empty">Няма поръчки</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Една поръчка: редакция в чернова, смяна на статуса, печат. */
export function PurchaseOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [lines, setLines] = useState([]);
  const [partner, setPartner] = useState(null);
  const [note, setNote] = useState('');
  const [expected, setExpected] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kandidati, setKandidati] = useState(null);
  const [dostavka, setDostavka] = useState('');

  function priemi(d) {
    setO(d);
    setLines(d.items.map((i) => ({
      productId: i.product_id, name: i.name, unit: i.unit,
      quantity: Number(i.quantity), price: i.price != null ? Number(i.price) : null,
      nalichnost: i.nalichnost, min: i.min_qty,
    })));
    setPartner({ id: d.partner_id, name: d.partner_name });
    setNote(d.note ?? '');
    setExpected(d.expected_date ? String(d.expected_date).slice(0, 10) : '');
    setDirty(false);
  }

  useEffect(() => {
    api.get(`/api/purchase-orders/${id}`).then(priemi).catch((e) => setError(e.message));
  }, [id]);

  if (error && !o) return <div className="error">{error}</div>;
  if (!o) return <div className="card"><div className="empty">Зареждане…</div></div>;

  const chernova = o.status === 'draft';
  const promeni = (fn) => { setLines(fn); setDirty(true); };

  async function zapishi() {
    setBusy(true); setError(null);
    try {
      priemi(await api.put(`/api/purchase-orders/${id}`, {
        partnerId: partner?.id,
        expectedDate: expected || null,
        note: note || null,
        lines: lines.map((l) => ({ productId: l.productId, quantity: Number(l.quantity), price: l.price })),
      }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function status(novStatus, extra = {}) {
    setBusy(true); setError(null);
    try {
      if (dirty) await zapishi();
      priemi(await api.post(`/api/purchase-orders/${id}/status`, { status: novStatus, ...extra }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function iztrii() {
    if (!window.confirm('Да се изтрие ли черновата?')) return;
    try {
      await api.del(`/api/purchase-orders/${id}`);
      navigate('/purchase-orders?tab=orders');
    } catch (e) { setError(e.message); }
  }

  async function pokaziDostavki() {
    setKandidati(await api.get(`/api/purchase-orders/${id}/candidate-deliveries`));
  }

  const suma = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.price || 0), 0);

  return (
    <>
      <div className="page-head">
        <h1>Поръчка № {o.order_no}</h1>
        <span className={PO_STATUS_CLASS[o.status]}>{PO_STATUS_LABEL[o.status]}</span>
        <div className="spacer" />
        <button type="button" onClick={() => navigate('/purchase-orders?tab=orders')}>Всички поръчки</button>
        <button type="button" onClick={() => window.open(`/print/purchase-order/${id}`, '_blank')}>
          Печат
        </button>
      </div>
      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="toolbar">
          <label className="field" style={{ minWidth: 300 }}>
            Доставчик
            {chernova
              ? <Lookup url="/api/partners" value={partner?.id ?? null} label={partner?.name ?? ''}
                  onPick={(pid, name) => { setPartner(pid ? { id: pid, name } : null); setDirty(true); }} />
              : <strong><Link to={`/partners/${o.partner_id}`}>{o.partner_name}</Link></strong>}
          </label>
          <label className="field">
            Дата
            <span>{date(o.order_date)}</span>
          </label>
          <label className="field">
            Очаквана доставка
            <input type="date" value={expected} disabled={!chernova}
              onChange={(e) => { setExpected(e.target.value); setDirty(true); }} />
          </label>
          <label className="field">
            Склад
            <span>{o.warehouse_name}</span>
          </label>
          <label className="field" style={{ flex: 1, minWidth: 220 }}>
            Бележка
            <input value={note} disabled={!chernova} maxLength={500}
              onChange={(e) => { setNote(e.target.value); setDirty(true); }} />
          </label>
        </div>

        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 36 }}>№</th>
              <th>Стока</th>
              <th className="num">Наличност</th>
              <th className="num">Мин.</th>
              <th className="num" style={{ width: 110 }}>Количество</th>
              <th style={{ width: 50 }}>Мярка</th>
              <th className="num">Посл. цена</th>
              <th className="num">≈ Стойност</th>
              {chernova && <th style={{ width: 30 }} />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={`${l.productId}-${i}`}>
                <td className="muted">{i + 1}</td>
                <td><Link to={`/products/${l.productId}`}>{l.name}</Link></td>
                <td className="num">{l.nalichnost != null ? quantity(l.nalichnost) : ''}</td>
                <td className="num">{l.min != null ? quantity(l.min) : ''}</td>
                <td className="num">
                  {chernova
                    ? <input type="number" min="0" step="1" style={{ width: 80, textAlign: 'right' }}
                        value={l.quantity}
                        onChange={(e) => promeni((s) => s.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
                    : quantity(l.quantity)}
                </td>
                <td>{l.unit}</td>
                <td className="num">{l.price != null ? money(l.price) : ''}</td>
                <td className="num">{l.price != null ? money(Number(l.quantity || 0) * l.price) : ''}</td>
                {chernova && (
                  <td>
                    <button type="button" className="link" title="махни"
                      onClick={() => promeni((s) => s.filter((_, j) => j !== i))}>×</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={7}>Общо без ДДС (по последни доставни цени)</th>
              <th className="num">{money(suma)}</th>
              {chernova && <th />}
            </tr>
          </tfoot>
        </table>

        {chernova && (
          <div className="toolbar" style={{ marginTop: 8 }}>
            <label className="field" style={{ minWidth: 340 }}>
              Добави стока
              <ProductLookup value={null} label="" placeholder="търсене на стока"
                onPick={async (pid, name) => {
                  if (!pid) return;
                  const p = await api.get(`/api/products/${pid}`).catch(() => null);
                  promeni((s) => [...s, {
                    productId: pid, name, unit: p?.unit ?? 'бр.', quantity: 1,
                    price: p?.purchase_price != null ? Number(p.purchase_price) : null,
                    nalichnost: null, min: p?.min_qty ?? null,
                  }]);
                }} />
            </label>
          </div>
        )}

        <div className="toolbar" style={{ marginTop: 12 }}>
          {chernova && (
            <>
              <button type="button" onClick={iztrii} disabled={busy}>Изтрий черновата</button>
              <div className="spacer" />
              {dirty && <button type="button" onClick={zapishi} disabled={busy || !lines.length}>Запиши</button>}
              <button type="button" disabled={busy} onClick={() => status('cancelled')}>Откажи</button>
              <button type="button" className="primary" disabled={busy || !lines.length || !partner}
                onClick={() => status('sent')}>
                Изпратена към доставчика
              </button>
            </>
          )}
          {o.status === 'sent' && (
            <>
              <button type="button" disabled={busy} onClick={() => status('cancelled')}>Откажи</button>
              <div className="spacer" />
              {kandidati === null ? (
                <button type="button" className="primary" onClick={pokaziDostavki}>Получена…</button>
              ) : (
                <>
                  <label className="field" style={{ minWidth: 280 }}>
                    С коя доставка пристигна?
                    <select value={dostavka} onChange={(e) => setDostavka(e.target.value)}>
                      <option value="">— без свързване —</option>
                      {kandidati.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.doc_number} · {date(d.doc_date)} · {money(d.total_base)}
                          {d.invoice_no ? ` · ф-ра ${d.invoice_no}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="primary" disabled={busy}
                    onClick={() => status('received', { purchaseDocumentId: dostavka ? Number(dostavka) : null })}>
                    Отбележи получена
                  </button>
                </>
              )}
            </>
          )}
          {(o.status === 'received' || o.status === 'cancelled') && (
            <span className="muted">
              {PO_STATUS_LABEL[o.status]} на {date(o.closed_at)} от {o.closed_by_name}
              {o.purchase_document_id && (
                <> · доставка <Link to={`/documents/${o.purchase_document_id}`}>{o.purchase_doc_number}</Link></>
              )}
            </span>
          )}
        </div>
        <p className="hint">
          Създадена {date(o.created_at)} от {o.created_by_name}
          {o.sent_at && <> · изпратена {date(o.sent_at)} от {o.sent_by_name}</>}
        </p>
      </div>
    </>
  );
}

/** Печатен вид — за изпращане на доставчика. */
export function PrintPurchaseOrder() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/api/purchase-orders/${id}`).then(setO).catch((e) => setError(e.message));
  }, [id]);
  if (error) return <div className="error" style={{ margin: 20 }}>{error}</div>;
  if (!o) return <div className="empty">Зареждане…</div>;

  return (
    <>
      <div className="print-toolbar">
        <button className="primary" onClick={() => window.print()}>Печат</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => window.close()}>Затвори</button>
      </div>
      <div className="print-page">
        <div className="print-title">
          <h1>ПОРЪЧКА № {o.order_no}</h1>
          <div className="sub">от {date(o.order_date)} · Доктор Принтер ЕООД</div>
        </div>
        <p>
          <strong>До:</strong> {o.partner_name}
          {o.partner_eik ? `, ЕИК ${o.partner_eik}` : ''}
          {o.partner_city || o.partner_address
            ? `, ${[o.partner_city, o.partner_address].filter(Boolean).join(', ')}` : ''}
          <br />
          <strong>Доставка до:</strong> склад {o.warehouse_name}
          {o.expected_date ? ` · очаквана на ${date(o.expected_date)}` : ''}
        </p>
        <table className="print-lines">
          <thead>
            <tr>
              <th style={{ width: '8mm' }}>№</th>
              <th>Наименование</th>
              <th>Код</th>
              <th style={{ width: '14mm' }}>Мярка</th>
              <th className="num" style={{ width: '22mm' }}>Количество</th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((it) => (
              <tr key={it.id}>
                <td>{it.line_no}</td>
                <td>{it.name}</td>
                <td>{it.code ?? ''}</td>
                <td>{it.unit}</td>
                <td className="num">{quantity(it.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {o.note && <p><strong>Бележка:</strong> {o.note}</p>}
        <div className="signatures">
          <div><div className="line" />Поръчал: {o.created_by_name}</div>
        </div>
      </div>
    </>
  );
}
